import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ListChecks, Check, Trash2, Plus, Search } from 'lucide-react';
import { ColumnHeader, KebabMenu } from './columnChrome';
import Modal from '../ui/Modal';
import { taskService } from '../../services/taskService';
import { groupTasksByDay } from '../../utils/taskGroups';
import { getActiveTimezone } from '../../utils/timezone';
import { stripAccents } from '../../utils/accentFold';
import { useLanguage } from '../../i18n';
import { logger } from '../../utils/logger';

// ─────────────────────────────────────────────────────────────────────────
// TaskListColumn — the Task List (#437), the right-hand column of the
// "Notes & Task List" panel.
//
// A persistent personal checklist with NO time attached. Layout (the card):
//   * tasks added TODAY sit at the top with no header;
//   * earlier days follow, newest first, each under a thin horizontal line that
//     carries its date as DD/MM/YYYY in the small meta type the Contacts module
//     uses for its month groups;
//   * inside a day the order is the order they were added, and a completed task
//     KEEPS its place — it is only struck through and faded.
//
// Every task is equally manageable from chat (Main Chat + Telegram) through the
// create_tasks / list_tasks / update_tasks / delete_tasks tools. Those tools
// emit task_* events (in-stream on the web, via the live-update feed from a
// channel), which land here as omnis:task-* window events — the "Real-time
// Parity" the card asks for, through the same mechanism every other panel uses.
// ─────────────────────────────────────────────────────────────────────────

const TaskCheckbox = ({ checked, onToggle, label, t }) => (
    <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        aria-label={checked
            ? t('taskMarkNotDone', 'Mark "{label}" as not done', { label })
            : t('taskMarkDone', 'Mark "{label}" as done', { label })}
        data-testid="task-checkbox"
        data-checked={checked ? 'true' : 'false'}
        onClick={onToggle}
        className={`mt-[1px] flex-shrink-0 w-[22px] h-[22px] rounded-[7px] border-[1.5px] flex items-center justify-center transition-colors ${checked
            ? 'bg-pai-green border-pai-green text-[#05170B]'
            : 'border-white/40 bg-transparent hover:border-white/80'}`}
    >
        {checked && <Check size={14} strokeWidth={3} />}
    </button>
);

const TaskRow = ({ task, onToggle, onSave, onDelete, t }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(task.content);
    const inputRef = useRef(null);

    useEffect(() => { if (!editing) setDraft(task.content); }, [task.content, editing]);
    useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

    const commit = () => {
        const next = draft.trim();
        setEditing(false);
        if (next && next !== task.content) onSave(task, next);
        else setDraft(task.content);
    };

    return (
        <div
            data-testid="task-item"
            data-task-id={task.id}
            data-completed={task.is_completed ? 'true' : 'false'}
            className="group flex items-start gap-2.5 rounded-row border border-white/[0.09] bg-white/[0.025] px-[14px] py-3 transition-colors hover:bg-white/[0.05]"
        >
            <TaskCheckbox checked={!!task.is_completed} onToggle={() => onToggle(task)} label={task.content} t={t} />
            <div className="min-w-0 flex-1">
                {editing ? (
                    <input
                        ref={inputRef}
                        data-testid="task-edit-input"
                        value={draft}
                        maxLength={500}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={commit}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); commit(); }
                            if (e.key === 'Escape') {
                                // Escape cancels the EDIT, never the panel behind it.
                                e.stopPropagation();
                                setDraft(task.content);
                                setEditing(false);
                            }
                        }}
                        className="pai-input w-full h-8 px-2 text-[14px] focus:outline-none"
                    />
                ) : (
                    <button
                        type="button"
                        data-testid="task-text"
                        onClick={() => setEditing(true)}
                        title={t('taskEditHint', 'Click to edit')}
                        className={`block w-full text-left text-[14.5px] leading-[1.4] break-words [overflow-wrap:anywhere] transition-colors ${task.is_completed
                            ? 'line-through text-white/[0.38]'
                            : 'text-white'}`}
                    >
                        {task.content}
                    </button>
                )}
            </div>
            <button
                type="button"
                data-testid="task-delete"
                onClick={() => onDelete(task)}
                aria-label={t('taskDelete', 'Delete task')}
                title={t('taskDelete', 'Delete task')}
                className="flex-shrink-0 p-1 text-pai-faint opacity-0 group-hover:opacity-100 pai-touch-reveal transition-opacity hover:text-pai-red focus-visible:opacity-100"
            >
                <Trash2 size={15} />
            </button>
        </div>
    );
};

// The "+" pop-up (#488 PO review): one text field, Add / Cancel. Enter adds,
// Escape cancels (owned by <Modal>, so the panel underneath stays open).
const NewTaskDialog = ({ onAdd, onClose, t }) => {
    const [text, setText] = useState('');
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);

    const submit = async () => {
        const content = text.trim();
        if (!content || adding) return;
        setAdding(true);
        setError(null);
        try {
            await onAdd(content);
            onClose();
        } catch (err) {
            setError(err?.message || t('taskSaveFailed', "Couldn't save the task."));
            setAdding(false);
        }
    };

    return (
        <Modal
            onClose={onClose}
            zIndex="z-[70]"
            size="sm"
            initialFocus={inputRef}
            cardTestId="task-new-dialog"
            ariaLabel={t('taskNewTitle', 'New task')}
            cardClassName="p-5"
        >
            <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
                <h3 className="text-[16px] font-bold text-white mb-3">{t('taskNewTitle', 'New task')}</h3>
                <input
                    ref={inputRef}
                    type="text"
                    data-testid="task-new-input"
                    value={text}
                    maxLength={500}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={t('taskAddPlaceholder', 'Add a task…')}
                    className="pai-input w-full h-[42px] px-3 text-sm focus:outline-none"
                />
                {error && (
                    <div data-testid="task-new-error" className="pt-2 text-[12px] text-pai-red">{error}</div>
                )}
                <div className="flex gap-2 mt-4">
                    <button
                        type="button"
                        data-testid="task-add-cancel"
                        onClick={onClose}
                        className="flex-1 px-3 py-2 text-[13px] font-semibold rounded-ctl border border-pai text-pai-secondary transition-colors hover:text-white hover:border-pai-strong"
                    >
                        {t('commonCancel', 'Cancel')}
                    </button>
                    <button
                        type="submit"
                        data-testid="task-add"
                        disabled={!text.trim() || adding}
                        className="flex-1 px-3 py-2 text-[13px] font-bold rounded-ctl border border-pai-green/50 bg-pai-green/[0.12] text-pai-green transition-colors hover:bg-pai-green/[0.2] disabled:opacity-40"
                    >
                        {t('commonAdd', 'Add')}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

// Accent- and case-insensitive substring match (the Notes search folds the
// same way, server-side).
const matchesSearch = (task, needle) => !needle
    || stripAccents(task.content || '').toLowerCase().includes(needle);

// `dragProps` / `layoutControls` / `menuItems`: the Notes & Task List layout
// chrome (#488), passed straight through to the header and the kebab.
const TaskListColumn = ({ dragProps = null, layoutControls = null, menuItems = [] }) => {
    const { t } = useLanguage();
    const [tasks, setTasks] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [search, setSearch] = useState('');
    const [newOpen, setNewOpen] = useState(false);
    const [error, setError] = useState(null);
    // 'completed' | 'all' | null — the inline confirmation for the kebab's
    // destructive actions (kept in the column, never a browser dialog).
    const [confirmScope, setConfirmScope] = useState(null);
    // Last-ISSUED-wins (the #294 lesson): a slow list response must never land
    // after a newer one and repaint stale state.
    const reqRef = useRef(0);

    const load = useCallback(async () => {
        const ticket = ++reqRef.current;
        try {
            const res = await taskService.list();
            if (ticket !== reqRef.current) return;
            setTasks(res?.tasks || []);
            setLoaded(true);
        } catch (err) {
            logger.warn('TASKS', `load tasks failed: ${err?.message || err}`);
            if (ticket === reqRef.current) setLoaded(true);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const onChange = () => load();
        window.addEventListener('omnis:task-created', onChange);
        window.addEventListener('omnis:task-updated', onChange);
        window.addEventListener('omnis:task-deleted', onChange);
        window.addEventListener('focus', onChange);
        return () => {
            window.removeEventListener('omnis:task-created', onChange);
            window.removeEventListener('omnis:task-updated', onChange);
            window.removeEventListener('omnis:task-deleted', onChange);
            window.removeEventListener('focus', onChange);
        };
    }, [load]);

    const needle = stripAccents(search.trim()).toLowerCase();
    const groups = useMemo(
        () => groupTasksByDay(tasks.filter((x) => matchesSearch(x, needle)), { timeZone: getActiveTimezone() }),
        [tasks, needle],
    );
    const completedCount = tasks.filter((x) => x.is_completed).length;

    // Throws on failure: the dialog stays open and shows the error.
    const addTask = async (content) => {
        await taskService.create(content);
        await load();
    };

    const toggle = async (task) => {
        const next = !task.is_completed;
        // Optimistic: the tick lands instantly; a failure puts it back.
        setTasks((cur) => cur.map((x) => (x.id === task.id ? { ...x, is_completed: next } : x)));
        try {
            await taskService.update(task.id, { is_completed: next });
        } catch (err) {
            logger.warn('TASKS', `toggle failed: ${err?.message || err}`);
            setTasks((cur) => cur.map((x) => (x.id === task.id ? { ...x, is_completed: !next } : x)));
        }
    };

    const saveText = async (task, content) => {
        setTasks((cur) => cur.map((x) => (x.id === task.id ? { ...x, content } : x)));
        try {
            await taskService.update(task.id, { content });
        } catch (err) {
            setError(err?.message || t('taskSaveFailed', "Couldn't save the task."));
            load();
        }
    };

    const removeOne = async (task) => {
        setTasks((cur) => cur.filter((x) => x.id !== task.id));
        try {
            await taskService.remove(task.id);
        } catch (err) {
            if (err?.status !== 404) load();
        }
    };

    const runConfirmed = async () => {
        const scope = confirmScope;
        setConfirmScope(null);
        try {
            await taskService.bulkDelete(scope === 'all' ? { all: true } : { completed: true });
        } catch (err) {
            logger.warn('TASKS', `bulk delete failed: ${err?.message || err}`);
        }
        load();
    };

    return (
        <div data-testid="tasks-column" className="flex-1 min-w-0 min-h-0 flex flex-col pai-card overflow-hidden">
            <ColumnHeader
                icon={<ListChecks size={18} />}
                accent="green"
                title={t('taskListTitle', 'Task List')}
                count={tasks.length}
                dragProps={dragProps}
                layoutControls={layoutControls}
                menu={(
                    <KebabMenu
                        buttonTestId="tasks-menu-button"
                        menuTestId="tasks-menu"
                        ariaLabel={t('taskMenuLabel', 'Task List menu')}
                        items={[
                            ...menuItems,
                            {
                                key: 'clear-completed',
                                testId: 'tasks-menu-clear-completed',
                                label: t('taskClearCompleted', 'Delete completed tasks'),
                                icon: <Check size={14} />,
                                onClick: () => setConfirmScope('completed'),
                            },
                            {
                                key: 'delete-all',
                                testId: 'tasks-menu-delete-all',
                                label: t('taskDeleteAll', 'Delete all tasks'),
                                icon: <Trash2 size={14} />,
                                onClick: () => setConfirmScope('all'),
                            },
                        ]}
                    />
                )}
                t={t}
            />
            {/* #488 PO review: search is the primary action; adding a task is the
                "+" beside it, which opens NewTaskDialog. */}
            <div className="px-3 pt-3 pb-1 flex-shrink-0 flex items-center gap-2">
                <div className="relative flex-1 min-w-0">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35 pointer-events-none" />
                    <input
                        type="text"
                        data-testid="tasks-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('taskSearchPlaceholder', 'Search tasks…')}
                        className="pai-input w-full h-[42px] pl-9 pr-3 text-sm focus:outline-none"
                    />
                </div>
                <button
                    type="button"
                    data-testid="task-new"
                    onClick={() => setNewOpen(true)}
                    aria-label={t('taskAdd', 'Add task')}
                    title={t('taskAdd', 'Add task')}
                    className="pai-icon-btn w-[42px] h-[42px] flex-shrink-0"
                >
                    <Plus size={18} />
                </button>
            </div>
            {error && (
                <div data-testid="tasks-error" className="px-4 pt-1 text-[12px] text-pai-red">{error}</div>
            )}
            <div data-testid="tasks-list" className="flex-1 min-h-0 overflow-y-auto px-3 py-3">
                {loaded && tasks.length === 0 ? (
                    <div data-testid="tasks-empty" className="text-center text-[13px] text-white/40 py-8">
                        {t('taskEmpty', 'No tasks yet. Add one above, or ask PAI.')}
                    </div>
                ) : loaded && groups.length === 0 ? (
                    <div data-testid="tasks-no-results" className="text-center text-[13px] text-white/40 py-8">
                        {t('taskNoResults', 'No tasks match your search.')}
                    </div>
                ) : groups.map((g) => (
                    <div
                        key={g.key}
                        data-testid="task-group"
                        data-day={g.key}
                        data-today={g.isToday ? 'true' : 'false'}
                        className="mb-3 last:mb-0"
                    >
                        {/* Today carries NO header (the card). Every earlier day sits
                            under a thin line carrying its date in the Contacts
                            module's small meta type. */}
                        {!g.isToday && (
                            <div data-testid="task-day-separator" className="flex items-center gap-2.5 my-2.5 px-1">
                                <span className="h-px flex-1 bg-white/[0.12]" />
                                <span data-testid="task-day-label" className="tnum text-[10px] tracking-wide font-semibold text-pai-meta">
                                    {g.label}
                                </span>
                                <span className="h-px flex-1 bg-white/[0.12]" />
                            </div>
                        )}
                        <div className="space-y-2">
                            {g.tasks.map((task) => (
                                <TaskRow
                                    key={task.id}
                                    task={task}
                                    onToggle={toggle}
                                    onSave={saveText}
                                    onDelete={removeOne}
                                    t={t}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            {confirmScope && (
                <div data-testid="tasks-confirm-bar" className="px-3 py-3 border-t border-pai-hairline flex-shrink-0">
                    <p className="text-[12.5px] text-pai-secondary mb-2">
                        {confirmScope === 'all'
                            ? t('taskConfirmDeleteAll', 'Delete all {n} tasks? This cannot be undone.', { n: tasks.length })
                            : t('taskConfirmDeleteCompleted', 'Delete {n} completed tasks? This cannot be undone.', { n: completedCount })}
                    </p>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            data-testid="tasks-confirm-delete"
                            onClick={runConfirmed}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[13px] font-semibold rounded-ctl text-pai-red border border-pai-red/40 bg-pai-red/[0.08] transition-colors hover:bg-pai-red/[0.16] hover:border-pai-red/60"
                        >
                            <Trash2 size={14} /> {t('commonDelete', 'Delete')}
                        </button>
                        <button
                            type="button"
                            data-testid="tasks-confirm-cancel"
                            onClick={() => setConfirmScope(null)}
                            className="flex-1 px-3 py-2 text-[13px] font-semibold rounded-ctl border border-pai text-pai-secondary transition-colors hover:text-white hover:border-pai-strong"
                        >
                            {t('commonCancel', 'Cancel')}
                        </button>
                    </div>
                </div>
            )}
            {newOpen && <NewTaskDialog onAdd={addTask} onClose={() => setNewOpen(false)} t={t} />}
        </div>
    );
};

export default TaskListColumn;
